import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import Slider, { SliderProps } from '@mui/material/Slider';
import { FieldValues, useController } from 'react-hook-form';

import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps extends Omit<SliderProps, 'onChange'> {
    label?: string;
    className?: string;
    helperText?: string;
    name: string;
    range?: boolean;
    onChange?: (name: string, value: number | number[]) => void;
}

export const SliderField = <TFieldValues extends FieldValues>({
    control: formControl,
    label,
    className,
    helperText,
    name,
    range,
    onChange,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const control = asTypedControl<number | number[]>(name, formControl);
    const { field } = useController({ name, control });
    const { value, onChange: fieldOnChange } = field;

    const [curValue, setCurValue] = useState(value);
    const [timer, setTimer] = useState<NodeJS.Timeout>();

    const handleChange = (newValue: number | number[]) => {
        if (value === newValue) return;

        setCurValue(newValue);

        // Add debounce before setting new field value
        if (timer) clearTimeout(timer);

        setTimer(
            setTimeout(() => {
                onChange?.(name, newValue);

                fieldOnChange(newValue);
            }, 500)
        );
    };

    useEffect(() => {
        if (value !== curValue) setCurValue(value);

        // Clear timer when component is unmounted
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [value]);

    return (
        <FormControl
            fullWidth
            className={className}
            margin="normal"
            variant="standard"
        >
            <Typography>{label}</Typography>
            <Box sx={{ mt: 1, mx: 0.75 }}>
                <Slider
                    defaultValue={0}
                    value={curValue}
                    valueLabelDisplay="auto"
                    onChange={(_event, value) => handleChange(value)}
                    {...other}
                />
            </Box>
            {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};
